import { FC } from 'react';

import { push } from 'connected-react-router';

import { HSpacingContainer, VSpacingContainer } from '@hh.ru/magritte-ui';
import Link from '@hh.ru/redux-spa-middleware/lib/Link';

import Button, { ButtonAppearance, ButtonKind, ButtonScale } from 'bloko/blocks/button';
import { HoverTip, Tip } from 'bloko/blocks/drop';
import DropInfo, { InfoLayer, InfoPlacement, InfoTheme } from 'bloko/blocks/drop/Info';
import { TipPlacement } from 'bloko/blocks/drop/Tip';
import BlokoLink, { LinkAppearance, LinkKind } from 'bloko/blocks/link';
import Text from 'bloko/blocks/text';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { setBannedCampaignId } from 'store/models/createCampaign';
import { useDispatch } from 'store/utils';

import styles from 'components/Banner/Actions/Message/BannedForAllSourcesTooltip/BannedForAllSourcesTooltip.module.less';

interface BannedForAllSourcesTooltipProps {
    bannedForAllSources: boolean;
    bannedAsAdvertisement: boolean;
    bannersCount: number;
    campaignId: number;
    bannerId: number;
    moderationReason: string;
}

const TrlKeys = {
    banned: 'banner.actions.message.status.banned',
    tip: {
        part1: 'banner.actions.message.status.banned.tip.part1',
        part2: 'banner.actions.message.status.banned.tip.part2',
        part3: 'banner.actions.message.status.banned.tip.part3',
        edit: 'banner.actions.message.status.banned.tip.edit',
        copy: 'banner.actions.message.status.banned.tip.copy',
        fewBanners: 'banner.actions.message.status.banned.tip.fewBanners',
        moderationReasonTitle: 'banner.actions.social.modal.title',
    },
};

export const BannedForAllSourcesTooltip: FC<BannedForAllSourcesTooltipProps> = ({
    bannedForAllSources,
    bannedAsAdvertisement,
    bannersCount,
    campaignId,
    bannerId,
    moderationReason,
}) => {
    const trl = useTrls();
    const dispatch = useDispatch();

    const [showInfo, setShowInfoOn, setShowInfoOff] = useOnOffState(false);

    const isSingleBanner = bannersCount === 1;

    const tipContent = bannedAsAdvertisement ? (
        <VSpacingContainer default={12}>
            {isSingleBanner ? (
                <>
                    <Text>{trl(TrlKeys.tip.part1)}</Text>
                    <Text>{trl(TrlKeys.tip.part2)}</Text>
                    <Text>{trl(TrlKeys.tip.part3)}</Text>
                </>
            ) : (
                <Text>{trl(TrlKeys.tip.fewBanners)}</Text>
            )}
            <HSpacingContainer default={10}>
                <Button
                    Element={Link}
                    to={`/new/campaign/${campaignId}/banner/${bannerId}/`}
                    appearance={ButtonAppearance.Outlined}
                    kind={ButtonKind.Inversed}
                    onClick={setShowInfoOff}
                    scale={ButtonScale.Small}
                >
                    {trl(TrlKeys.tip.edit)}
                </Button>
                {isSingleBanner && (
                    <Button
                        appearance={ButtonAppearance.Outlined}
                        kind={ButtonKind.Inversed}
                        onClick={() => {
                            dispatch(setBannedCampaignId(campaignId));
                            dispatch(push(`/new/campaign/${campaignId}/banner/${bannerId}/copy`));
                        }}
                        scale={ButtonScale.Small}
                    >
                        {trl(TrlKeys.tip.copy)}
                    </Button>
                )}
            </HSpacingContainer>
        </VSpacingContainer>
    ) : (
        <VSpacingContainer default={8}>
            <Text strong>{trl(TrlKeys.tip.moderationReasonTitle)}</Text>
            {moderationReason.split('\n').map((reason, index) => (
                <Text key={index}>{reason}</Text>
            ))}
        </VSpacingContainer>
    );

    return (
        <>
            {bannedAsAdvertisement && (
                <DropInfo
                    show={showInfo}
                    onClose={setShowInfoOff}
                    layer={InfoLayer.AboveOverlayContent}
                    host={document.body}
                    theme={InfoTheme.Dark}
                    render={() => tipContent}
                    placement={InfoPlacement.TopEnd}
                    showCloseButton
                >
                    <div data-qa="ad-block__campaign-state" onClick={setShowInfoOn}>
                        <BlokoLink
                            Element="div"
                            disableVisited
                            appearance={LinkAppearance.Pseudo}
                            kind={LinkKind.Tertiary}
                        >
                            <div className={styles.bannedForAllSourcesState}>{trl(TrlKeys.banned)}</div>
                        </BlokoLink>
                    </div>
                </DropInfo>
            )}
            {bannedForAllSources && !bannedAsAdvertisement && (
                <HoverTip render={() => tipContent} placement={TipPlacement.Top} host={document.body} DropElement={Tip}>
                    <div data-qa="ad-block__campaign-state">
                        <BlokoLink
                            Element="div"
                            disableVisited
                            appearance={LinkAppearance.Pseudo}
                            kind={LinkKind.Tertiary}
                        >
                            <div className={styles.bannedForAllSourcesState}>{trl(TrlKeys.banned)}</div>
                        </BlokoLink>
                    </div>
                </HoverTip>
            )}
        </>
    );
};
